import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Search,
  MoreVertical,
  UserPlus,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Users,
  Target,
  GraduationCap,
  Plane,
  Home,
  DollarSign,
  Briefcase,
  RefreshCw,
  Calendar,
  User,
  Award,
  Loader2,
  ShieldCheck,
  XCircle,
  ArrowRight,
  UserCheck,
  Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import ServiceTypeFilterCards, {
  SERVICE_CONFIG,
} from '../../components/admin/ServiceTypeFilterCards';
import FormDataViewer from '../../components/services/hub/FormDataViewer';

const STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: {
    label: 'Pending Assignment',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Clock,
  },
  ASSIGNED: {
    label: 'Assigned',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: UserPlus,
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: RefreshCw,
  },
  ON_HOLD: {
    label: 'On Hold',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    icon: AlertCircle,
  },
  COMPLETED: {
    label: 'Completed',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: AlertCircle,
  },
};

const APPROVAL_STATUS_CONFIG = {
  PENDING_APPROVAL: {
    label: 'Pending Approval',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Clock,
  },
  APPROVED: {
    label: 'Approved',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: ShieldCheck,
  },
  REJECTED: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: XCircle,
  },
};

// Status tab configuration
const STATUS_TABS = [
  { key: 'all', label: 'All Requests', status: null },
  {
    key: 'pending',
    label: 'Pending Assignment',
    status: 'PENDING_ADMIN_ASSIGNMENT',
  },
  { key: 'assigned', label: 'Assigned', status: 'ASSIGNED' },
  { key: 'inProgress', label: 'In Progress', status: 'IN_PROGRESS' },
  { key: 'completed', label: 'Completed', status: 'COMPLETED' },
];

export default function AdminServiceRequests() {
  const navigate = useNavigate();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedCounselor, setSelectedCounselor] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [pendingAgentRequestsCount, setPendingAgentRequestsCount] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAgentRequestsCount();
  }, []);

  const fetchAgentRequestsCount = async () => {
    try {
      const response = await adminAPI.getAgentRequestsPendingCount();
      setPendingAgentRequestsCount(response.count || 0);
    } catch (error) {
      console.error('Failed to fetch agent requests count:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, counselorsRes, agentsRes] = await Promise.all([
        adminAPI.getAllServiceRequests().catch(err => {
          console.error('Error fetching service requests:', err);
          return { serviceRequests: [] };
        }),
        adminAPI.getCounselors().catch(err => {
          console.error('Error fetching counselors:', err);
          return { counselors: [] };
        }),
        adminAPI.getAgents().catch(err => {
          console.error('Error fetching agents:', err);
          return { agents: [] };
        }),
      ]);

      setServiceRequests(requestsRes.serviceRequests || requestsRes.data || []);
      setCounselors(counselorsRes.counselors || []);
      setAgents(agentsRes.agents || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRequest = async () => {
    if (!selectedCounselor) {
      toast.error('Please select a counselor');
      return;
    }

    setAssigning(true);
    try {
      const requestId =
        selectedRequest.requestId ||
        selectedRequest.serviceRequestId ||
        selectedRequest._id;
      await adminAPI.assignServiceRequest(requestId, {
        counselorId: selectedCounselor || undefined,
        agentId: selectedAgent || undefined,
      });

      toast.success('Service request assigned successfully!');
      setAssignModalOpen(false);
      setSelectedRequest(null);
      setSelectedCounselor('');
      setSelectedAgent('');
      fetchData();
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error(error.response?.data?.message || 'Failed to assign request');
    } finally {
      setAssigning(false);
    }
  };

  const handleUpdateStatus = async (request, newStatus) => {
    try {
      const requestId = request.requestId || request._id;
      await adminAPI.updateServiceRequestStatus(requestId, {
        status: newStatus,
      });
      toast.success('Status updated successfully');
      fetchData();
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    }
  };

  const openAssignModal = (request, e) => {
    if (e) e.stopPropagation();
    setSelectedRequest(request);
    setSelectedCounselor(request.assignedCounselor?._id || '');
    setSelectedAgent(request.assignedAgent?._id || '');
    setAssignModalOpen(true);
  };

  const openDetailModal = (request, e) => {
    if (e) e.stopPropagation();
    setSelectedRequest(request);
    setDetailModalOpen(true);
  };

  const openDeleteModal = (request, e) => {
    if (e) e.stopPropagation();
    setRequestToDelete(request);
    setDeleteModalOpen(true);
  };

  const handleDeleteRequest = async () => {
    if (!requestToDelete) return;
    setDeleting(true);
    try {
      const requestId =
        requestToDelete.requestId ||
        requestToDelete.serviceRequestId ||
        requestToDelete._id;
      await adminAPI.deleteServiceRequest(requestId);
      toast.success('Service request deleted successfully');
      setDeleteModalOpen(false);
      setRequestToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(
        error.response?.data?.error || 'Failed to delete service request',
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleRowClick = request => {
    const studentId = request.student?.studentId || request.studentId;
    if (studentId) {
      navigate(`/admin/students/${studentId}`);
    }
  };

  const getServiceName = serviceType => {
    return SERVICE_CONFIG[serviceType]?.name || serviceType;
  };

  const getServiceIcon = serviceType => {
    return SERVICE_CONFIG[serviceType]?.icon || FileText;
  };

  const getServiceColor = serviceType => {
    return SERVICE_CONFIG[serviceType]?.color || 'from-gray-500 to-gray-600';
  };

  const getStudentName = request => {
    if (request.student?.user?.firstName) {
      return `${request.student.user.firstName} ${request.student.user.lastName || ''}`;
    }
    if (request.student?.firstName) {
      return `${request.student.firstName} ${request.student.lastName || ''}`;
    }
    return 'Unknown Student';
  };

  const getStudentEmail = request => {
    return request.student?.user?.email || request.student?.email || '';
  };

  const filteredRequests = serviceRequests.filter(request => {
    const studentName = getStudentName(request).toLowerCase();
    const serviceName = getServiceName(request.serviceType).toLowerCase();
    const searchLower = searchTerm.toLowerCase();

    const matchesSearch =
      studentName.includes(searchLower) || serviceName.includes(searchLower);
    const matchesStatus =
      statusFilter === 'all' || request.status === statusFilter;
    const matchesServiceType =
      !serviceTypeFilter || request.serviceType === serviceTypeFilter;

    return matchesSearch && matchesStatus && matchesServiceType;
  });

  const stats = {
    all: serviceRequests.length,
    pending: serviceRequests.filter(
      r => r.status === 'PENDING_ADMIN_ASSIGNMENT',
    ).length,
    assigned: serviceRequests.filter(r => r.status === 'ASSIGNED').length,
    inProgress: serviceRequests.filter(r => r.status === 'IN_PROGRESS').length,
    completed: serviceRequests.filter(r => r.status === 'COMPLETED').length,
  };

  const getActiveCount = value => {
    if (typeof value === 'number') return value;
    if (Array.isArray(value)) return value.length;
    if (value && typeof value === 'object' && typeof value.length === 'number')
      return value.length;
    return 0;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-service-requests-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">
              Service Requests
            </h1>
            <p className="text-gray-600">
              Manage and assign service requests to counselors
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" disabled={loading}>
            <RefreshCw
              className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
        </div>

        {/* Agent Requests Banner */}
        {/* {pendingAgentRequestsCount > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Card
              className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200 cursor-pointer hover:shadow-md transition-all"
              onClick={() => navigate('/admin/agent-requests')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">Agent Service Requests</h3>
                      <Badge className="bg-indigo-500 text-white">
                        {pendingAgentRequestsCount} Pending
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">
                      Service requests initiated by agents awaiting your approval
                    </p>
                  </div>
                </div>
                <Button variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-100">
                  Review Requests
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          </motion.div>
        )} */}

        {/* Status Tabs */}
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map(tab => {
            const count =
              tab.key === 'all'
                ? stats.all
                : tab.key === 'pending'
                  ? stats.pending
                  : tab.key === 'assigned'
                    ? stats.assigned
                    : tab.key === 'inProgress'
                      ? stats.inProgress
                      : stats.completed;
            const isActive = statusFilter === (tab.status || 'all');

            return (
              <Button
                key={tab.key}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(tab.status || 'all')}
                className="gap-2"
              >
                {tab.label}
                <Badge
                  variant={isActive ? 'secondary' : 'outline'}
                  className="ml-1"
                >
                  {count}
                </Badge>
              </Button>
            );
          })}
          {/* Agent Requests Tab */}
          {/* <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/agent-requests')}
            className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          >
            Agent Requests
            <Badge className="bg-indigo-500 text-white ml-1">
              {pendingAgentRequestsCount}
            </Badge>
          </Button> */}
        </div>

        {/* Service Type Filter Cards */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">
              Filter by Service Type
            </h3>
            {serviceTypeFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setServiceTypeFilter(null)}
                className="text-gray-500"
              >
                Clear filter
              </Button>
            )}
          </div>
          <ServiceTypeFilterCards
            requests={serviceRequests}
            selectedType={serviceTypeFilter}
            onSelectType={setServiceTypeFilter}
          />
        </Card>

        {/* Search */}
        <Card className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by student name or service..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-requests-input"
            />
          </div>
        </Card>

        {/* Service Requests Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <FileText className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-lg font-medium">No Service Requests Found</p>
              <p className="text-sm">
                Try adjusting your search or filter criteria
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="service-requests-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Service
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Initiated By
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Approval
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Applied
                    </th>
                    <th className="text-right px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <AnimatePresence>
                    {filteredRequests.map((request, index) => {
                      const StatusIcon =
                        STATUS_CONFIG[request.status]?.icon || Clock;
                      const ServiceIcon = getServiceIcon(request.serviceType);
                      const statusConfig =
                        STATUS_CONFIG[request.status] ||
                        STATUS_CONFIG.PENDING_ADMIN_ASSIGNMENT;
                      const serviceColor = getServiceColor(request.serviceType);
                      const requestId = request.requestId || request._id;

                      return (
                        <motion.tr
                          key={requestId}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ delay: index * 0.02 }}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleRowClick(request)}
                          data-testid={`request-row-${requestId}`}
                        >
                          {/* Service */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-10 h-10 bg-gradient-to-br ${serviceColor} rounded-lg flex items-center justify-center flex-shrink-0`}
                              >
                                {typeof ServiceIcon === 'string' ? (
                                  <img
                                    src={ServiceIcon}
                                    alt="Service"
                                    className="w-6 h-6 object-contain"
                                  />
                                ) : (
                                  <ServiceIcon className="w-5 h-5 text-white" />
                                )}
                              </div>
                              <span className="font-medium text-gray-900">
                                {getServiceName(request.serviceType)}
                              </span>
                            </div>
                          </td>

                          {/* Student */}
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900">
                                {getStudentName(request)}
                              </p>
                              <p className="text-sm text-gray-500">
                                {getStudentEmail(request)}
                              </p>
                            </div>
                          </td>

                          {/* Initiated By */}
                          <td className="px-6 py-4">
                            {request.representative ? (
                              <div className="flex flex-col gap-0.5">
                                <Badge
                                  variant="outline"
                                  className="bg-indigo-50 text-indigo-700 border-indigo-200 w-fit"
                                >
                                  <UserCheck className="w-3 h-3 mr-1" />
                                  {request.representative.firstName}{' '}
                                  {request.representative.lastName}
                                </Badge>
                                <span className="text-xs text-gray-500 ml-1">
                                  {request.representative.role === 'rep1'
                                    ? 'Internal Rep'
                                    : request.representative.role === 'rep2'
                                      ? 'Senior Rep'
                                      : request.representative.role === 'rep3'
                                        ? 'Partner Rep'
                                        : request.representative.role ===
                                            'agent'
                                          ? 'Agent'
                                          : 'Representative'}
                                  {request.representative.representativeLevel
                                    ? ` (Level ${request.representative.representativeLevel})`
                                    : ''}
                                </span>
                              </div>
                            ) : typeof request.representativeName === 'string' &&
                              request.representativeName.trim() ? (
                              <div className="flex flex-col gap-1">
                                <Badge
                                  variant="outline"
                                  className="bg-indigo-50 text-indigo-700 border-indigo-200 w-fit"
                                >
                                  <UserCheck className="w-3 h-3 mr-1" />
                                  {request.representativeName.trim()}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="bg-gray-50 text-gray-600 border-gray-200 w-fit text-[10px] px-2 py-0.5"
                                >
                                  Representative
                                </Badge>
                              </div>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-gray-50 text-gray-600 border-gray-200 w-fit"
                              >
                                <User className="w-3 h-3 mr-1" />
                                {getStudentName(request) || 'Student'}
                              </Badge>
                            )}
                          </td>

                          {/* Approval Status */}
                          <td className="px-6 py-4">
                            {request.isAgentInitiated &&
                            request.agentApprovalStatus ? (
                              <Badge
                                className={
                                  APPROVAL_STATUS_CONFIG[
                                    request.agentApprovalStatus
                                  ]?.color || 'bg-gray-100'
                                }
                              >
                                {(() => {
                                  const ApprovalIcon =
                                    APPROVAL_STATUS_CONFIG[
                                      request.agentApprovalStatus
                                    ]?.icon;
                                  return ApprovalIcon ? (
                                    <ApprovalIcon className="w-3 h-3 mr-1" />
                                  ) : null;
                                })()}
                                {APPROVAL_STATUS_CONFIG[
                                  request.agentApprovalStatus
                                ]?.label || request.agentApprovalStatus}
                              </Badge>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>

                          {/* Assignment Status */}
                          <td className="px-6 py-4">
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </td>

                          {/* Applied Time */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <Calendar className="w-3 h-3" />
                              {formatDistanceToNow(
                                new Date(
                                  request.appliedAt || request.createdAt,
                                ),
                                { addSuffix: true },
                              )}
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4">
                            <div
                              className="flex items-center justify-end gap-2"
                              onClick={e => e.stopPropagation()}
                            >
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={e => openDetailModal(request, e)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>

                              {(request.status === 'PENDING_ADMIN_ASSIGNMENT' ||
                                !request.assignedCounselor) && (
                                <Button
                                  size="sm"
                                  onClick={e => openAssignModal(request, e)}
                                  data-testid={`assign-btn-${requestId}`}
                                >
                                  <UserPlus className="w-4 h-4" />
                                </Button>
                              )}

                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() =>
                                      navigate(
                                        `/admin/service-management/${request.serviceRequestId || request.requestId || request._id}`,
                                      )
                                    }
                                  >
                                    <ArrowRight className="w-4 h-4 mr-2" />
                                    Manage Service
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openAssignModal(request)}
                                  >
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    {request.assignedCounselor
                                      ? 'Reassign'
                                      : 'Assign'}
                                  </DropdownMenuItem>
                                  {request.status !== 'IN_PROGRESS' && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleUpdateStatus(
                                          request,
                                          'IN_PROGRESS',
                                        )
                                      }
                                    >
                                      <RefreshCw className="w-4 h-4 mr-2" />
                                      Mark In Progress
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleUpdateStatus(request, 'ON_HOLD')
                                    }
                                  >
                                    <AlertCircle className="w-4 h-4 mr-2" />
                                    Put On Hold
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleUpdateStatus(request, 'COMPLETED')
                                    }
                                  >
                                    <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                                    Mark Completed
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleUpdateStatus(request, 'CANCELLED')
                                    }
                                  >
                                    <XCircle className="w-4 h-4 mr-2 text-red-500" />
                                    Cancel Request
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openDeleteModal(request)}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Results Count */}
        {!loading && filteredRequests.length > 0 && (
          <div className="text-sm text-gray-600">
            Showing {filteredRequests.length} of {serviceRequests.length}{' '}
            requests
          </div>
        )}

        {/* Assignment Modal */}
        <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                Assign Service Request
              </DialogTitle>
              <DialogDescription>
                Assign a counselor to handle this service request.
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">
                    {getServiceName(selectedRequest.serviceType)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Student: {getStudentName(selectedRequest)}
                  </p>
                </div>

                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  Select a counselor to assign this service request
                </p>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Assign Counselor
                    </label>
                    <Select
                      value={selectedCounselor || 'none'}
                      onValueChange={val =>
                        setSelectedCounselor(val === 'none' ? '' : val)
                      }
                    >
                      <SelectTrigger data-testid="counselor-select">
                        <SelectValue placeholder="Select a counselor (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Counselor</SelectItem>
                        {counselors.map(counselor => {
                          const activeCount =
                            getActiveCount(counselor.activeServiceRequests) ||
                            getActiveCount(counselor.assignedStudents);
                          return (
                            <SelectItem
                              key={counselor.userId || counselor._id}
                              value={counselor.userId || counselor._id}
                            >
                              <div className="flex items-center gap-2">
                                <span>
                                  {counselor.firstName} {counselor.lastName}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ({activeCount} active)
                                </span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setAssignModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAssignRequest}
                    disabled={assigning || !selectedCounselor}
                  >
                    {assigning ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Assigning...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Assign Request
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] p-0 rounded-2xl border-0 shadow-2xl flex flex-col overflow-hidden">
            {selectedRequest &&
              (() => {
                const Icon = getServiceIcon(selectedRequest.serviceType);
                const statusCfg = STATUS_CONFIG[selectedRequest.status];
                const StatusIcon = statusCfg?.icon || Clock;
                const approvalCfg = selectedRequest.agentApprovalStatus
                  ? APPROVAL_STATUS_CONFIG[selectedRequest.agentApprovalStatus]
                  : null;

                return (
                  <>
                    {/* ── Header ── */}
                    <div className="shrink-0 p-6 border-b border-gray-100">
                      <div className="flex items-start gap-4">
                        <div
                          className={`w-12 h-12 bg-gradient-to-br ${getServiceColor(selectedRequest.serviceType)} rounded-xl flex items-center justify-center shrink-0 shadow-sm`}
                        >
                          {typeof Icon === 'string' ? (
                            <img
                              src={Icon}
                              alt="Service"
                              className="w-7 h-7 object-contain"
                            />
                          ) : (
                            <Icon className="w-6 h-6 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <DialogTitle className="text-lg font-bold text-gray-900 leading-tight">
                            {getServiceName(selectedRequest.serviceType)}
                          </DialogTitle>
                          <DialogDescription className="sr-only">
                            Service request details
                          </DialogDescription>
                          <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            <Badge
                              className={`${statusCfg?.color} flex items-center gap-1 text-xs`}
                            >
                              <StatusIcon className="w-3 h-3" />
                              {statusCfg?.label}
                            </Badge>
                            {selectedRequest.isAgentInitiated && (
                              <Badge
                                variant="outline"
                                className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs flex items-center gap-1"
                              >
                                <UserCheck className="w-3 h-3" />
                                Agent Initiated
                              </Badge>
                            )}
                            {approvalCfg && (
                              <Badge className={`${approvalCfg.color} text-xs`}>
                                {approvalCfg.label}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {/* Request ID pill */}
                        <span className="text-[10px] font-mono text-gray-400 bg-gray-50 border border-gray-100 rounded-md px-2 py-1 shrink-0 hidden sm:block">
                          #
                          {(
                            selectedRequest.serviceRequestId ||
                            selectedRequest._id ||
                            ''
                          )
                            .slice(-8)
                            .toUpperCase()}
                        </span>
                      </div>
                    </div>

                    {/* ── Scrollable body ── */}
                    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                      {/* Agent-initiated callout */}
                      {selectedRequest.isAgentInitiated && (
                        <div className="flex items-start gap-3 p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
                          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                            <UserCheck className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-indigo-800">
                              Agent-Initiated Request
                            </p>
                            <p className="text-xs text-indigo-600 mt-0.5 leading-relaxed">
                              Submitted by an agent on behalf of their student.
                            </p>
                            {selectedRequest.initiatingAgent && (
                              <p className="text-xs text-indigo-700 mt-1.5 font-medium">
                                Agent:{' '}
                                {selectedRequest.initiatingAgent.firstName}{' '}
                                {selectedRequest.initiatingAgent.lastName}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* ── Section: Student ── */}
                      <div>
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5" />
                          Student Information
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            {
                              label: 'Full Name',
                              value: getStudentName(selectedRequest),
                            },
                            {
                              label: 'Email Address',
                              value:
                                getStudentEmail(selectedRequest) ||
                                'Not provided',
                            },
                            {
                              label: 'Phone Number',
                              value:
                                selectedRequest.student?.phone ||
                                'Not provided',
                            },
                            {
                              label: 'Applied On',
                              value: format(
                                new Date(
                                  selectedRequest.appliedAt ||
                                    selectedRequest.createdAt,
                                ),
                                'PPp',
                              ),
                            },
                          ].map(({ label, value }) => (
                            <div
                              key={label}
                              className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100"
                            >
                              <p className="text-[11px] text-gray-400 font-medium mb-0.5">
                                {label}
                              </p>
                              <p className="text-sm font-semibold text-gray-900 break-all">
                                {value}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* ── Section: Assignment ── */}
                      <div>
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5" />
                          Assignment Details
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            {
                              label: 'Assigned Counselor',
                              value: selectedRequest.assignedCounselor
                                ? `${selectedRequest.assignedCounselor.firstName} ${selectedRequest.assignedCounselor.lastName}`
                                : 'Not assigned',
                              empty: !selectedRequest.assignedCounselor,
                            },
                            {
                              label: 'Assigned Agent',
                              value: selectedRequest.assignedAgent
                                ? `${selectedRequest.assignedAgent.firstName} ${selectedRequest.assignedAgent.lastName}`
                                : 'None',
                              empty: !selectedRequest.assignedAgent,
                            },
                          ].map(({ label, value, empty }) => (
                            <div
                              key={label}
                              className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100"
                            >
                              <p className="text-[11px] text-gray-400 font-medium mb-0.5">
                                {label}
                              </p>
                              <p
                                className={`text-sm font-semibold ${empty ? 'text-gray-400 italic' : 'text-gray-900'}`}
                              >
                                {value}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* ── Section: Pre-Application Form ── */}
                      {selectedRequest.formData && (
                        <div>
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" />
                            Pre-Application Form
                          </p>
                          <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
                            <FormDataViewer
                              serviceType={selectedRequest.serviceType}
                              formData={selectedRequest.formData}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ── Footer ── */}
                    <div className="shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50/60 rounded-b-2xl flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2">
                      <Button
                        variant="ghost"
                        className="text-gray-500 text-sm"
                        onClick={() => setDetailModalOpen(false)}
                      >
                        Close
                      </Button>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <Button
                          variant="outline"
                          className="text-sm rounded-xl"
                          onClick={() => {
                            setDetailModalOpen(false);
                            handleRowClick(selectedRequest);
                          }}
                        >
                          <User className="w-4 h-4 mr-1.5" />
                          Student Profile
                        </Button>
                        {(selectedRequest.status ===
                          'PENDING_ADMIN_ASSIGNMENT' ||
                          !selectedRequest.assignedCounselor) && (
                          <Button
                            className="text-sm rounded-xl"
                            onClick={() => {
                              setDetailModalOpen(false);
                              openAssignModal(selectedRequest);
                            }}
                          >
                            <UserPlus className="w-4 h-4 mr-1.5" />
                            Assign Counselor
                          </Button>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Service Request
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone. The service request and all
                associated tasks, messages, and records will be permanently
                deleted.
              </DialogDescription>
            </DialogHeader>
            {requestToDelete && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-100 text-sm text-red-700">
                <p className="font-medium">
                  {getServiceName(requestToDelete.serviceType)}
                </p>
                <p className="mt-1 text-red-600">
                  Student: {getStudentName(requestToDelete)}
                </p>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteModalOpen(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteRequest}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Permanently
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
